import React from "react";

/**
 * Parses markdown-style formatting in AI messages and returns React nodes
 * Currently supports:
 * - **text** for bold text
 * - URLs (http://, https://, www.) as clickable links
 * 
 * Designed to be easily extensible for future formatting requirements
 */

interface TextSegment {
  type: 'text' | 'bold' | 'link';
  content: string;
  url?: string;
  startIndex: number;
  endIndex: number;
}

export const parseMessage = (text: string): React.ReactNode => {
  if (!text) return text;

  const segments: TextSegment[] = [];
  let key = 0;

  // Find all bold segments (**text**)
  const boldRegex = /\*\*(.+?)\*\*/g;
  let boldMatch: RegExpExecArray | null;
  while ((boldMatch = boldRegex.exec(text)) !== null) {
    segments.push({
      type: 'bold',
      content: boldMatch[1],
      startIndex: boldMatch.index,
      endIndex: boldMatch.index + boldMatch[0].length,
    });
  }

  // Find all URL segments (http://, https://, or www.)
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  let urlMatch: RegExpExecArray | null;
  while ((urlMatch = urlRegex.exec(text)) !== null) {
    // Check if this URL overlaps with any bold segment
    const overlapsBold = segments.some(
      (seg) =>
        seg.type === 'bold' &&
        urlMatch!.index! < seg.endIndex &&
        urlMatch!.index! + urlMatch![0].length > seg.startIndex
    );

    if (!overlapsBold) {
      let url = urlMatch[0];
      // Add https:// if URL starts with www.
      if (url.startsWith('www.')) {
        url = `https://${url}`;
      }
      segments.push({
        type: 'link',
        content: urlMatch[0],
        url: url,
        startIndex: urlMatch.index!,
        endIndex: urlMatch.index! + urlMatch[0].length,
      });
    }
  }

  // Sort segments by start index
  segments.sort((a, b) => a.startIndex - b.startIndex);

  // Build React nodes
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  segments.forEach((segment) => {
    // Add text before the segment
    if (segment.startIndex > lastIndex) {
      const textBefore = text.substring(lastIndex, segment.startIndex);
      if (textBefore) {
        parts.push(
          <React.Fragment key={`text-${key++}`}>
            {textBefore}
          </React.Fragment>
        );
      }
    }

    // Add the formatted segment
    if (segment.type === 'bold') {
      parts.push(
        <strong key={`bold-${key++}`}>
          {segment.content}
        </strong>
      );
    } else if (segment.type === 'link') {
      parts.push(
        <a
          key={`link-${key++}`}
          href={segment.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline' }}
        >
          {segment.content}
        </a>
      );
    }

    lastIndex = segment.endIndex;
  });

  // Add remaining text after last segment
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    if (remainingText) {
      parts.push(
        <React.Fragment key={`text-${key++}`}>
          {remainingText}
        </React.Fragment>
      );
    }
  }

  // If no formatting found, return original text
  if (parts.length === 0) {
    return text;
  }

  return <>{parts}</>;
};
